import React from "react";
import {
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import QrCodeIcon from "@mui/icons-material/QrCode";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IChooseWhatsAppDIalogProps, IWAServiceProvider } from "./ShareViaWhatsApp.types";
import { Dialog } from "../Dialogs/Dialog";

interface IProviderConfig {
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  recommended?: boolean;
  warning?: string;
  warningNote?: string;
  borderColor: string;
  iconBg: string;
}

const PROVIDER_CONFIG: Record<IWAServiceProvider, IProviderConfig> = {
  META: {
    label: "Meta WhatsApp",
    sublabel: "Official Meta Business API",
    icon: <WhatsAppIcon sx={{ color: "#25D366", fontSize: "1.8rem" }} />,
    recommended: true,
    borderColor: "#25D366",
    iconBg: "#f0fdf4",
  },
  BAILEY: {
    label: "WhatsApp QR",
    sublabel: "QR-based WhatsApp",
    icon: <QrCodeIcon sx={{ color: "#1976d2", fontSize: "1.8rem" }} />,
    warning: "Bulk messaging may lead to number restrictions or blocking.",
    warningNote: "For campaigns and high-volume messaging, use Meta WhatsApp.",
    borderColor: "#e0e0e0",
    iconBg: "#f0f4ff",
  },
};

export const ChooseWhatsAppDIalog: React.FC<IChooseWhatsAppDIalogProps> =
  React.memo(({ open, onClose, onSelect, providers }) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        size="sm"
        title="Send via WhatsApp"
        subtitle="Choose which WhatsApp service to use"
      >
        <DialogContent sx={{ px: 2, pt: 1, pb: 0 }}>
          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {providers.map((provider) => {
              const config = PROVIDER_CONFIG[provider as IWAServiceProvider];
              if (!config) return null;
              return (
                <ListItemButton
                  key={provider}
                  onClick={() => onSelect(provider as IWAServiceProvider)}
                  sx={{
                    borderRadius: "12px",
                    border: "1.5px solid",
                    borderColor: config.borderColor,
                    p: 1.5,
                    alignItems: "flex-start",
                    position: "relative",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {/* Recommended chip — top right */}
                  {config.recommended && (
                    <Chip
                      label="Recommended"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        bgcolor: "#25D366",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 20,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  )}

                  {/* Icon box */}
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        bgcolor: config.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {config.icon}
                    </Box>
                  </ListItemIcon>

                  {/* Text */}
                  <Box sx={{ flex: 1, pr: config.recommended ? 8 : 0 }}>
                    <Typography fontWeight={700} fontSize="0.95rem" lineHeight={1.3}>
                      {config.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="0.78rem"
                      mt={0.25}
                    >
                      {config.sublabel}
                    </Typography>

                    {/* Warning block */}
                    {config.warning && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          bgcolor: "#fff8e1",
                          borderRadius: "8px",
                          border: "1px solid #ffe082",
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" gap={0.5}>
                          <WarningAmberIcon
                            sx={{ fontSize: "0.95rem", color: "#f57c00", mt: "1px" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: "#e65100", fontWeight: 500, lineHeight: 1.5 }}
                          >
                            {config.warning}
                          </Typography>
                        </Box>
                        {config.warningNote && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
                          >
                            {config.warningNote}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
          <Button onClick={onClose} variant="outlined" size="small" color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

ChooseWhatsAppDIalog.displayName = "ChooseWhatsAppDIalog";